import { useTranslation } from "@sagot/i18n";
import { Field, Input, Textarea } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "../../../lib/zod-resolver";
import type { NewSource } from "./use-knowledge";

const K = "deployKnowledge.knowledge.add";
/** Server limits (`QAPair`, `TextSourceIn`). */
const QUESTION_MAX = 2000;
const ANSWER_MAX = 20000;
const TITLE_MAX = 300;
const TEXT_MAX = 100000;

export interface PanelProps {
  formId: string;
  audiences: number[];
  onSubmit: (source: NewSource) => void;
}

export function QaPanel({ formId, audiences, onSubmit }: PanelProps) {
  const { t } = useTranslation();
  const schema = z.object({
    question: z.string().trim().min(1, t(`${K}.questionRequired`)).max(QUESTION_MAX),
    answer: z.string().trim().min(1, t(`${K}.answerRequired`)).max(ANSWER_MAX),
  });
  const { register, handleSubmit, formState } = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { question: "", answer: "" } });
  return (
    <form id={formId} noValidate className="space-y-4" onSubmit={(event) => void handleSubmit((values) => { onSubmit({ type: "qa", ...values, audiences }); })(event)}>
      <Field label={t(`${K}.question`)} error={formState.errors.question?.message}>
        <Input maxLength={QUESTION_MAX} {...register("question")} />
      </Field>
      <Field label={t(`${K}.answer`)} hint={t(`${K}.qaHint`)} error={formState.errors.answer?.message}>
        <Textarea rows={4} maxLength={ANSWER_MAX} {...register("answer")} />
      </Field>
    </form>
  );
}

export function TextPanel({ formId, audiences, onSubmit }: PanelProps) {
  const { t } = useTranslation();
  const schema = z.object({
    title: z.string().trim().min(1, t(`${K}.textTitleRequired`)).max(TITLE_MAX),
    text: z.string().trim().min(1, t(`${K}.textBodyRequired`)).max(TEXT_MAX),
  });
  const { register, handleSubmit, formState } = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { title: "", text: "" } });
  return (
    <form id={formId} noValidate className="space-y-4" onSubmit={(event) => void handleSubmit((values) => { onSubmit({ type: "text", ...values, audiences }); })(event)}>
      <Field label={t(`${K}.textTitle`)} error={formState.errors.title?.message}>
        <Input maxLength={TITLE_MAX} {...register("title")} />
      </Field>
      <Field label={t(`${K}.textBody`)} error={formState.errors.text?.message}>
        <Textarea rows={6} maxLength={TEXT_MAX} {...register("text")} />
      </Field>
    </form>
  );
}
